import { useEffect, useState } from "react";

import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import {
  CloudIcon,
  HomeIcon,
  QuestionMarkCircleIcon,
  StarIcon,
} from "@heroicons/react/outline";

import Notes from "../../components/Notes/Notes";
import { getFullAddress } from "../../utils/getAddress";
import useUserContext from "../../contexts/UserContext";
import Breadcrumbs from "../../components/Shared/Breadcrumbs";
import LoadingWheel from "../../components/Shared/LoadingWheel";
import usePropertiesSingle from "../../data/usePropertiesSingle";
import SwitchInput from "./../../components/Shared/Inputs/SwitchInput";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { ButtonInput } from "../../components/Shared/Inputs/ButtonInput";
import { ManualPropertyPostModal } from "../../components/SocialPosts/ManualPropertyPostModal";

function PropertyCard({
  title,
  TitleComponent,
  children,
  wrapperClasses,
  titleClasses,
}) {
  return (
    <div className={`bg-white divide-y ${wrapperClasses}`}>
      {TitleComponent ? (
        <TitleComponent />
      ) : (
        <div
          className={`prose-lg text-gray-500 font-medium py-3 px-5 ${titleClasses}`}
        >
          {title}
        </div>
      )}
      {children}
    </div>
  );
}

function PropertyDetails({ property }) {
  return (
    <PropertyCard
      title="Property Details"
      wrapperClasses="col-span-1 row-span-1"
    >
      <div className="text-gray-700 py-3 px-5">
        <div className="grid grid-cols-12 gap-y-2">
          <div className="col-span-1 flex items-center">
            <HomeIcon className="w-4 h-4" />
          </div>
          <div className="col-span-5">{property.bedrooms} Bedrooms</div>
          <div className="col-span-1 flex items-center">
            <CloudIcon className="w-4 h-4" />
          </div>
          <div className="col-span-5">{property.bathrooms} Bathrooms</div>
          <div className="col-span-1 flex items-center">
            <QuestionMarkCircleIcon className="w-4 h-4" />
          </div>
          <div className="col-span-11">
            Availability: {property.availability}
          </div>
          <div className="col-span-1 flex items-center">
            <StarIcon className="w-4 h-4" />
          </div>
          <div className="col-span-11">
            {property.department === "residential-lettings"
              ? `£${property.price} pw (£${property.fullPrice} p/month)`
              : `£${property.price}`}
          </div>
        </div>
      </div>
      <div>
        <h2 className="prose-lg text-gray-500 font-medium py-3 px-5">
          Property Features
        </h2>
        <ul className="grid text-gray-700 list-outside list-disc pl-8 grid-cols-2 gap-y-2">
          {property.features.map((feature, i) => {
            return (
              <li key={i} className="col-span-1">
                {feature.feature}
              </li>
            );
          })}
        </ul>
      </div>
    </PropertyCard>
  );
}

function MapView({ property }) {
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");

  useEffect(() => {
    setLatitude(property.latitude.replace("-.", "-0."));
    setLongitude(property.longitude.replace("-.", "-0."));
  }, [property]);

  return (
    <PropertyCard title="View on Map" wrapperClasses="col-span-1 row-span-1">
      <div className="py-3 px-5">
        <iframe
          title="Google map"
          style={{ border: 0 }}
          loading="lazy"
          allowFullScreen=""
          referrerPolicy="origin"
          src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyDipwICcP5ZhQHsgEpZL5ClGpR95mN6G7M&q=${latitude}%2C${longitude}`}
          width="100%"
          height="300px"
        ></iframe>
      </div>
    </PropertyCard>
  );
}

export default function PropertyView() {
  const {
    user: { agency },
  } = useUserContext();
  const { register, setValue, watch, reset } = useForm();
  const navigate = useNavigate();
  const { propertyId } = useParams();
  const {
    property: singleProperty,
    toggleFeatured,
    toggleWindow,
    toggleSocialPost,
    updateNotes,
    removeNote,
  } = usePropertiesSingle(propertyId);
  const { saveHandlers, deleteHandlers } =
    useDefaultCRUDHandlers("Property note");
  const [property, setProperty] = useState(undefined);
  const [propertyUrl, setPropertyUrl] = useState("");
  const [socialPostOpen, setSocialPostOpen] = useState(false);

  useEffect(() => {
    if (!singleProperty.isLoading) {
      let property = singleProperty.data;
      var department =
        property?.department === "residential-lettings"
          ? "property-to-rent"
          : "property-for-sale"; //property-for-sale or property-to-rent

      reset(property);
      setPropertyUrl(`${agency.domain}/${department}/${property.slug}/`);
      setProperty(property);
    }
  }, [singleProperty.isLoading, singleProperty.data]);

  const deleteNote = (note) => {
    var updateData = {
      noteId: note.id,
      propertyId: propertyId,
    };

    removeNote.mutate(updateData, deleteHandlers);
  };

  const addNote = (data) => {
    console.log("submitting", data);

    let mutateData = {
      propertyId: propertyId,
      newNote: {
        note: data.note,
      },
    };

    if (propertyId) {
      updateNotes.mutate(mutateData, saveHandlers);
    }
  };

  const handleSetFeatured = (name, value) => {
    toggleFeatured.mutate(propertyId, {
      onSuccess: () => {
        let updated = property;
        updated.isFeatured = value;
        setProperty(updated);
        setValue(name, value);
      },
      onError: (error) => {
        console.error("Error updating property:", error);
      },
    });
    // setValue(name, value);
  };

  const handleSetWindow = (name, value) => {
    toggleWindow.mutate(propertyId, {
      onSuccess: () => {
        let updated = property;
        updated.isWindowDisplay = value;
        setProperty(updated);
        setValue(name, value);
      },
      onError: (error) => {
        console.error("Error updating property:", error);
      },
    });
    // setValue(name, value);
  };

  const handleSocialPostToggle = (name, value) => {
    toggleSocialPost.mutate(propertyId, {
      onSuccess: () => {
        let updated = property;
        updated.socialDontPost = value;
        setProperty(updated);
        setValue(name, value);
      },
      onError: (error) => {
        console.error("Error updating property:", error);
      },
    });
  };

  const pages = [
    { to: "..", label: "Properties", current: false },
    {
      to: propertyId && property ? `${propertyId}` : "",
      label: propertyId && property ? `Property` : "New Property",
      current: true,
    },
  ];

  if (singleProperty.isLoading || !property) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="h-80 max-w-7xl flex items-center justify-center">
          <LoadingWheel width="w-12" height="h-12" />
        </div>
      </div>
    );
  } else {
    return (
      <>
        <ManualPropertyPostModal
          open={socialPostOpen}
          setOpen={() => setSocialPostOpen(!socialPostOpen)}
          propertyId={propertyId}
          agencyId={agency.id}
        />
        <div className="px-4 sm:px-6 lg:px-8">
          <Breadcrumbs pages={pages} />
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center">
              <h1 className="text-2xl font-semibold text-gray-900">
                <div
                  dangerouslySetInnerHTML={{ __html: getFullAddress(property) }}
                />
              </h1>
            </div>
          </div>
          <div className="mb-3">
            <h1 className="text-lg">
              {property.views} views | {property.enquiries} enquiries
            </h1>
          </div>
          <div className="flex items-center justify-end gap-x-4">
            {/* <SwitchInput
              label={"Exclude from Social Posts"}
              register={register}
              registerName="socialDontPost"
              setValue={handleSocialPostToggle}
              watch={watch}
              margin="mr-2"
              classes=""
            /> */}
            <SwitchInput
              label={"Featured"}
              register={register}
              registerName="isFeatured"
              setValue={handleSetFeatured}
              watch={watch}
              margin="mr-2"
              classes=""
            />
            <SwitchInput
              label={"Window Display"}
              register={register}
              registerName="isWindowDisplay"
              setValue={handleSetWindow}
              watch={watch}
              margin="mr-2"
              classes=""
            />
            {/* <ButtonInput
              label="Post to Socials"
              onClick={() => setSocialPostOpen(true)}
            /> */}
            <a href={propertyUrl} target="_blank" rel="noreferrer">
              <ButtonInput label="View on site" />
            </a>
          </div>
          <div className="my-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <PropertyDetails property={property} />
              <MapView property={property} />
            </div>
          </div>
          <div className="bg-white p-5 mb-10">
            <h1 className="pb-3 text-xl font-semibold text-gray-900 underline">
              Property Description
            </h1>
            <p
              dangerouslySetInnerHTML={{ __html: property.description }}
              className="leading-relaxed"
            ></p>
          </div>

          <Notes
            notes={property.notes}
            submitFunction={addNote}
            deleteFunction={deleteNote}
          />
        </div>
      </>
    );
  }
}
